<template>
	<div>
		<v-layout class="mx-2 my-2">
			<MessageAuthor :user="user" :message="message" :show-nova="showNova" :show-bot="showBot" />
			<div class="ml-3 mt-2" style="word-break: break-word; overflow-wrap: break-word; white-space: normal">
				<Flag v-if="!showNova" :lang="message.lang" />
				<Word v-for="word in message.words" :word="word" />
				<!-- temporary quick hack -->
				<div
					v-if="
						message.messageId ===
						gptQuestionsSchemaSpecializationStore.questionSchemaSpecializationOptions.newNotificationId
					"
				>
					<v-btn
						v-for="result in gptQuestionsSchemaSpecializationStore.questionSchemaSpecializationOptions
							.results"
						@click="setSpecialization(result)"
						class="mt-2"
						:class="{ 'btn-clicked': btnClicked }"
						:prepend-icon="btnClicked ? 'mdi-check-bold' : undefined"
						>{{
							symbolsStore.term({ terminology: graphStore.descriptor.enumItem.byId(result)?.terminology })
						}}
					</v-btn>
				</div>
			</div>
		</v-layout>
		<v-layout v-if="isNotification" style="line-height: 1.5rem; text-decoration: underline">
			{{ graphStore.descriptor.notification.byId(message.messageId)?.label }}
		</v-layout>
		<div v-if="isMe" v-for="translation in translations">
			<v-divider />
			<v-layout class="mx-2 my-2">
				<PlayIndicator :audio-id="translation.audioId" />
				<div class="mx-3 my-2"><Flag :lang="translation.lang" /> {{ translation.value }}</div>
			</v-layout>
		</div>
		<div
			v-if="
				!isMe &&
				message.lang != myLang &&
				message.messageId !==
					gptQuestionsSchemaSpecializationStore.questionSchemaSpecializationOptions.newNotificationId &&
				prefTranslation.value
			"
		>
			<v-divider />
			<v-layout class="mx-2 my-2">
				<PlayIndicator :audio-id="prefTranslation.audioId" />
				<div class="mx-3 my-2"><Flag :lang="prefTranslation.lang" /> {{ prefTranslation.value }}</div>
			</v-layout>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Message, Session } from "@lr-notitia/notitia-alternis/schemas";
import { ref, computed } from "vue";
import { useDomainStore } from "../../../../store/domain";
import { useSessionStore } from "../../../../store/alternis/session";
import { useGraphStore } from "../../../../store/graph";
import { useGptQuestionsSchemaSpecializationStore } from "../../../../store/gpt/questionsSchemaSpecialization";
import { useSymbolsStore } from "../../../../store/symbols";
import Word from "./Word.vue";
import Flag from "./Flag.vue";
import MessageAuthor from "./MessageAuthor.vue";
import PlayIndicator from "./PlayIndicator.vue";

const domainStore = useDomainStore();
const sessionStore = useSessionStore();
const graphStore = useGraphStore();
const gptQuestionsSchemaSpecializationStore = useGptQuestionsSchemaSpecializationStore();
const symbolsStore = useSymbolsStore();

const props = defineProps<{
	message: Message;
}>();

const btnClicked = ref(false);

const isMe = computed(() => props.message.userId === domainStore.account.id);
const showNova = computed(() => props.message.type === "system");
const showBot = computed(() => props.message.type === "bot");
const session = computed<Session>(() => sessionStore.session || { sessionId: "", users: [], messages: [], bots: [] });
const user = computed(() => session.value.users.find((u) => u.userId === props.message.userId));
const myLang = computed(() => session.value.users.find((u) => u.userId === domainStore.account.id)?.lang || "");
const translations = computed(() => Object.keys(props.message.translation).map((lang) => translation(lang)));
const prefTranslation = computed(() => {
	const lang = myLang.value;
	return translation(lang);
});

function translation(lang: string): { lang: string; value: string; audioId?: string } {
	if (!props.message.translation) return { lang, value: "No translation available" };
	if (lang in props.message.translation) return { lang, ...props.message.translation[lang] };
	return { lang, value: "" };
}
const isNotification = computed(() => {
	return props.message.type === "system" && !!graphStore.descriptor.notification.byId(props.message.messageId);
});

// temporary quick hack
function setSpecialization(enumItemId: string) {
	let conceptAttribute = graphStore.concept.attribute.byId(
		gptQuestionsSchemaSpecializationStore.questionSchemaSpecializationOptions.conceptAttrId
	);
	if (!conceptAttribute) return;
	let instanceAttribute = graphStore.instance.attributes.find((a) => a.concept === conceptAttribute?.id);
	if (!instanceAttribute) return;

	if (instanceAttribute.value === enumItemId) return;
	graphStore.instance.attribute.update(instanceAttribute.id, enumItemId).then(() => {
		btnClicked.value = true;
	});
}
</script>

<style scoped lang="scss">
.btn-clicked {
	border-width: 2px;
	border-radius: 12px;
	border-color: #4db6ac;
	color: #4db6ac;
}
</style>
